import { useCallback, useMemo, useState } from 'react';
import FileSaver from 'file-saver';
import { AttachmentResponse, FileResponse, InsertAttachmentRequest } from 'services/tenantManagementService';
import { Info } from '../Info/Info';
import { AttachmentIcon } from 'components/icons/icons';

export const useItems = (
	newAttachments: InsertAttachmentRequest[] = [],
	oldAttachments: AttachmentResponse[] = [],
	disabled: boolean = false,
	additionalInfoMap: { [key: number]: string } | undefined,
	removeNewAttachment: (attachment: InsertAttachmentRequest) => void,
	removeOldAttachment: ((attachmentId: number) => Promise<void>) | undefined,
	downloadOldAttachment?: (attachmentId: number) => Promise<FileResponse>
) => {
	const [deletingId, setDeletingId] = useState<number>();
	const [downloadingId, setDownloadingId] = useState<number>();

	const downloadOldAttachmentCallback = useCallback(
		async (id: number) => {
			if (!downloadOldAttachment) {
				return;
			}
			setDownloadingId(id);
			const response = await downloadOldAttachment(id);

			if (response.status === 200) {
				FileSaver.saveAs(response.data, response.fileName);
			}
			setDownloadingId(undefined);
		},
		[downloadOldAttachment]
	)

	const removeOldAttachmentCallback = useCallback(
		async (id: number) => {
			setDeletingId(id)
			removeOldAttachment && await removeOldAttachment(id);
			setDeletingId(undefined)
		},
		[removeOldAttachment]
	)

	const oldAttachmentsContent = useMemo(
		() => oldAttachments.map(attachment => (
			<Info
				key={attachment.id}
				text={attachment.name}
				additionalInfo={additionalInfoMap?.[attachment.id]}
				disabled={disabled || !removeOldAttachment}
				onClick={() => {
					downloadOldAttachmentCallback(attachment.id);
				}}
				onDelete={() => removeOldAttachmentCallback(attachment.id)}
				isDeletable={!!removeOldAttachment}
				isLoading={deletingId === attachment.id || downloadingId === attachment.id}
				Icon={AttachmentIcon}
			/>
		)),
		[oldAttachments, downloadOldAttachmentCallback, removeOldAttachment, removeOldAttachmentCallback, deletingId, disabled, downloadingId, additionalInfoMap]
	)

	const newAttachmentsContent = useMemo(
		() => newAttachments?.map((attachment, index) => (
			<Info
				key={index}
				text={attachment.name}
				disabled={disabled}
				isDeletable
				onDelete={() => removeNewAttachment(attachment)}
			/>
		)),
		[newAttachments, disabled, removeNewAttachment]
	)

	return useMemo(
		() => {
			return [
				...oldAttachmentsContent,
				...newAttachmentsContent
			]
		},
		[oldAttachmentsContent, newAttachmentsContent]
	)
}
